import { mkdtempSync, mkdirSync, rmSync } from "node:fs";
import { tmpdir } from "node:os";
import { join, resolve } from "node:path";
import { z } from "incur";
import { getApp, getAppBuild, type WhopApp } from "./api.js";
import {
	APP_CONFIG_FILENAME,
	findProjectDir,
	readAppConfig,
	writeAppConfig,
} from "./config.js";
import {
	commitAll,
	commitSnapshot,
	gitInit,
	gitInstalled,
	hasCommits,
	isDirty,
	isMerged,
	mergeSnapshot,
	nothingToMerge,
	repoStatus,
	resetHard,
	stashPop,
	stashPush,
} from "./git.js";
import { extractSourceArchive } from "./source-archive.js";
import { CommandAbort, downloadArchive } from "./shared.js";

export const pullOptions = z.object({
	app: z
		.string()
		.optional()
		.describe("App id (defaults to the project's linked app)"),
	build: z
		.string()
		.optional()
		.describe(
			"Pull this specific build's source (abld_xxx) instead of the current production build",
		),
	dir: z
		.string()
		.optional()
		.describe(
			"Target directory (defaults to the linked project, or ./<route> on a fresh machine)",
		),
	overwrite: z
		.boolean()
		.optional()
		.describe(
			"Skip the merge and write the archive files straight over the working tree",
		),
});

export const pullOutput = z.object({
	app_id: z.string(),
	build_id: z.string().optional(),
	dir: z.string(),
	status: z.enum(["merged", "up_to_date", "overwritten"]),
	autostash: z.enum(["popped", "kept"]).optional(),
});

type PullOptions = z.infer<typeof pullOptions>;
type PullOutput = z.infer<typeof pullOutput>;

function requireGitInstalled(): void {
	if (gitInstalled()) return;
	throw new CommandAbort({
		code: "GIT_REQUIRED",
		message:
			"`whop apps pull` needs git to merge the remote source safely, and git is not installed. Install it from https://git-scm.com/downloads and re-run.",
	});
}

type PullSource = {
	app: WhopApp;
	buildId?: string;
	sourceUrl: string;
};

/**
 * Resolves what to download. `--build` pins an exact build; otherwise the
 * app's current production build — pull means "give me what's deployed", and
 * defaulting to a possibly half-baked newer preview build would surprise.
 */
async function resolveSource(options: PullOptions): Promise<PullSource> {
	if (options.build) {
		let build;
		try {
			build = await getAppBuild(options.build);
		} catch (err) {
			throw new CommandAbort({
				code: "BUILD_NOT_FOUND",
				message: `Could not load build ${options.build}: ${err instanceof Error ? err.message : "unknown error"}`,
				retryable: true,
			});
		}
		if (!build.source_url) {
			throw new CommandAbort({
				code: "BUILD_HAS_NO_SOURCE",
				message: `Build ${build.id} has no source archive (it was uploaded without one).`,
			});
		}
		const appId = options.app ?? linkedAppId();
		if (!appId) {
			throw new CommandAbort({
				code: "APP_REQUIRED",
				message: `Builds don't carry their app id — pass --app app_xxx alongside --build, or run inside a project with ${APP_CONFIG_FILENAME}.`,
				retryable: true,
			});
		}
		return {
			app: await loadApp(appId),
			buildId: build.id,
			sourceUrl: build.source_url,
		};
	}

	const appId = options.app ?? linkedAppId();
	if (!appId) {
		throw new CommandAbort({
			code: "APP_REQUIRED",
			message: `No app specified. Pass --app app_xxx, or run inside a project with ${APP_CONFIG_FILENAME}. Use \`whop apps list\` to find the app id.`,
			retryable: true,
			cta: {
				commands: [
					{ command: "apps list", description: "Find the app id" },
					{
						command: "apps pull",
						description: "Re-run with --app <app_id>",
					},
				],
			},
		});
	}
	const app = await loadApp(appId);
	const production = app.production_web_build;
	if (!production?.source_url) {
		throw new CommandAbort({
			code: "APP_HAS_NO_SOURCE",
			message: `${app.name} (${app.id}) has no production build with a source archive to pull. Deploy from the machine that has the code, or pass --build abld_xxx for a specific build that has one.`,
		});
	}
	return { app, buildId: production.id, sourceUrl: production.source_url };
}

function linkedAppId(): string | null {
	const projectDir = findProjectDir();
	const config = projectDir ? readAppConfig(projectDir) : null;
	return config?.app_id ?? null;
}

async function loadApp(appId: string): Promise<WhopApp> {
	try {
		return await getApp(appId);
	} catch (err) {
		throw new CommandAbort({
			code: "APP_NOT_FOUND",
			message: `Could not load app ${appId}: ${err instanceof Error ? err.message : "unknown error"}`,
			retryable: true,
		});
	}
}

/**
 * Where the source lands: an explicit --dir wins; then the linked project
 * when it belongs to the pulled app; then ./<route> like `git clone` — never
 * an unrelated directory the user happens to be standing in.
 */
function resolveTargetDir(options: PullOptions, app: WhopApp): string {
	if (options.dir) return resolve(options.dir);
	const projectDir = findProjectDir();
	if (projectDir && readAppConfig(projectDir)?.app_id === app.id) {
		return projectDir;
	}
	const route = app.route;
	if (!route) {
		throw new CommandAbort({
			code: "DIR_REQUIRED",
			message: `${app.name} (${app.id}) has no route to name a directory after — pass --dir <path>.`,
			retryable: true,
		});
	}
	return resolve(`./${route}`);
}

function ensureUsableRepo(targetDir: string): void {
	const status = repoStatus(targetDir);
	if (status.kind === "none") {
		gitInit(targetDir);
		if (isDirty(targetDir)) {
			// Never be the thing holding the only copy: pre-existing files get a
			// commit before anything is written over them.
			commitAll(targetDir, "Local files before whop apps pull");
		}
		return;
	}
	if (status.kind === "nested") {
		throw new CommandAbort({
			code: "NESTED_IN_REPO",
			message: `${targetDir} is inside the git repository at ${status.toplevel}. Pull merges at the repository root only — run it in a standalone directory, or pass --overwrite to just write the files.`,
		});
	}
}

export async function runPullFlow(options: PullOptions): Promise<PullOutput> {
	requireGitInstalled();

	const source = await resolveSource(options);
	const { app } = source;
	const targetDir = resolveTargetDir(options, app);

	// Downloaded before the directory is created: a failed download used to leave
	// an empty directory behind, which reads as a checked-out app that is somehow
	// empty rather than as a pull that never happened.
	const zip = await downloadArchive(source.sourceUrl);
	mkdirSync(targetDir, { recursive: true });
	const linkConfig = {
		app_id: app.id,
		name: app.name,
		route: app.route ?? "",
	};

	if (options.overwrite) {
		extractSourceArchive(zip, targetDir);
		writeAppConfig(targetDir, linkConfig);
		recordOverwriteSnapshot(zip, targetDir, app.id, source.buildId);
		return {
			app_id: app.id,
			build_id: source.buildId,
			dir: targetDir,
			status: "overwritten",
			autostash: undefined,
		};
	}

	const snapshotDir = mkdtempSync(join(tmpdir(), "whop-pull-"));
	try {
		extractSourceArchive(zip, snapshotDir);

		ensureUsableRepo(targetDir);

		const message = `whop apps pull: ${app.id}${source.buildId ? ` @ ${source.buildId}` : ""}`;
		const snapshot = commitSnapshot(targetDir, snapshotDir, app.id, message);

		// Up to date = merging would introduce nothing. Ancestry covers machines
		// whose history came from pulls; nothingToMerge covers the machine that
		// deployed last, whose snapshot ref is parented on its own HEAD.
		if (
			hasCommits(targetDir) &&
			(isMerged(targetDir, snapshot.commit) ||
				nothingToMerge(targetDir, snapshot.commit))
		) {
			ensureLinked(targetDir, linkConfig);
			return {
				app_id: app.id,
				build_id: source.buildId,
				dir: targetDir,
				status: "up_to_date",
				autostash: undefined,
			};
		}

		if (!hasCommits(targetDir)) {
			resetHard(targetDir, snapshot.commit);
			ensureLinked(targetDir, linkConfig);
			return {
				app_id: app.id,
				build_id: source.buildId,
				dir: targetDir,
				status: "merged",
				autostash: undefined,
			};
		}

		// Mirrors `git pull --autostash`: local edits are stashed around the
		// merge and popped after. A pop that conflicts keeps the stash and says
		// so, rather than losing anything.
		const stashed =
			isDirty(targetDir) &&
			stashPush(targetDir, "whop apps pull autostash") === "stashed";

		const merge = mergeSnapshot(targetDir, snapshot.commit, message);
		if (merge.status === "conflicts") {
			throw new CommandAbort({
				code: "PULL_CONFLICTS",
				message: [
					`Merge conflicts in ${targetDir} — the remote source and your local code changed the same files:`,
					...merge.files.map((file) => `  ${file}`),
					"",
					"Resolve the conflict markers, then `git add -A && git commit` to finish the merge. Nothing was lost.",
					...(stashed
						? [
								"Your uncommitted changes are safe in the autostash — `git stash pop` after committing the merge.",
							]
						: []),
				].join("\n"),
			});
		}

		const autostash = stashed
			? stashPop(targetDir)
				? ("popped" as const)
				: ("kept" as const)
			: undefined;
		ensureLinked(targetDir, linkConfig);
		return {
			app_id: app.id,
			build_id: source.buildId,
			dir: targetDir,
			status: "merged",
			autostash,
		};
	} finally {
		rmSync(snapshotDir, { recursive: true, force: true });
	}
}

/**
 * `--overwrite` bypasses the merge, but the snapshot ref must still advance —
 * it records the last remote state this machine saw, and leaving it stale
 * would make the next normal pull merge against an outdated base. Only
 * possible at a repo root; elsewhere (nested dir, no git) overwrite stays a
 * plain file write.
 */
function recordOverwriteSnapshot(
	zip: Uint8Array,
	targetDir: string,
	appId: string,
	buildId: string | undefined,
): void {
	if (!gitInstalled() || repoStatus(targetDir).kind !== "root") return;
	const snapshotDir = mkdtempSync(join(tmpdir(), "whop-pull-"));
	try {
		extractSourceArchive(zip, snapshotDir);
		commitSnapshot(
			targetDir,
			snapshotDir,
			appId,
			`whop apps pull --overwrite: ${appId}${buildId ? ` @ ${buildId}` : ""}`,
		);
	} finally {
		rmSync(snapshotDir, { recursive: true, force: true });
	}
}

/**
 * The archive normally carries its own whop.app.json, so after a merge the
 * link is already right — writing unconditionally would dirty a just-cleaned
 * tree over formatting. Only write when the link is missing or wrong.
 */
function ensureLinked(
	targetDir: string,
	config: { app_id: string; name: string; route: string },
): void {
	if (readAppConfig(targetDir)?.app_id === config.app_id) return;
	writeAppConfig(targetDir, config);
}
